import { ReactNode, useState } from 'react'

import { ActionIcon, Collapse } from '@mantine/core'
import { Check, X } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'

type SettingsSectionCardProps = {
  title: ReactNode
  /** Optional content pinned to the right of the header (e.g. an admin badge). */
  headerRight?: ReactNode
  initialOpened?: boolean
  /** Whether to render the Edit/Save/Cancel controls at all. */
  editable?: boolean
  editing?: boolean
  onEdit?: () => void
  onSave?: () => void
  onCancel?: () => void
  saveDisabled?: boolean
  loading?: boolean
  children: ReactNode
}

/**
 * Collapsible bordered section card matching the Company settings tab style
 * (see CompanyDetailsPage cards). Provides the shared chevron + title header
 * chrome, with optional per-card Edit/Save/Cancel controls, so each settings
 * section renders consistently.
 */
const SettingsSectionCard = ({
  title,
  headerRight = null,
  initialOpened = true,
  editable = false,
  editing = false,
  onEdit,
  onSave,
  onCancel,
  saveDisabled = false,
  loading = false,
  children,
}: SettingsSectionCardProps) => {
  const [opened, setOpened] = useState(initialOpened)

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        {title}
        <div className="ml-auto flex items-center gap-2">
          {headerRight}
          {editable && !editing && (
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                if (!opened) setOpened(true)
                onEdit?.()
              }}
              className="text-doubleNickel-gray-700"
            >
              <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
              Edit
            </DNButton>
          )}
          {editable && editing && (
            <>
              <DNButton
                variant="transparent"
                onClick={(e) => {
                  e.stopPropagation()
                  onSave?.()
                }}
                disabled={saveDisabled || loading}
                className="text-doubleNickel-success-500"
              >
                <Check size={14} />
                Save
              </DNButton>
              <DNButton
                variant="transparent"
                onClick={(e) => {
                  e.stopPropagation()
                  onCancel?.()
                }}
                disabled={loading}
                className="text-doubleNickel-error-500"
              >
                <X size={14} />
                Cancel
              </DNButton>
            </>
          )}
        </div>
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-4 px-1 pt-2">{children}</div>
      </Collapse>
    </div>
  )
}

export default SettingsSectionCard
